import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { FaFilterCircleXmark } from 'react-icons/fa6';
import { FaFileExcel } from 'react-icons/fa';
import useChargeReports from '@/features/chargeReport/hooks/useChargeReports';
import { fetchChargeReports } from '@/features/chargeReport/services/chargeReportService';
import ChargeReportPagination from '@/features/chargeReport/components/ChargeReportPagination';
import useUsers from '@/features/user/hooks/useUsers';
import useCharges from '@/features/charge/hooks/useCharges';
import ChargeReportTable from '@/features/chargeReport/components/ChargeReportTable';
import exportChargeReportToExcel from '@/features/chargeReport/utils/exportExcel';
import ConfirmExportModal from '@/features/chargeReport/components/ConfirmExportModal';
const ChargeReportPage = () => {
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showExportModal, setShowExportModal] = useState(false);
    const { users } = useUsers(undefined, undefined, true);
    const { charges: chargeTypes } = useCharges(undefined, undefined, true);
    const { reports, meta, loading, error } = useChargeReports(currentPage, filters, false, rowsPerPage);
    const totalPages = meta ?
        (meta.totalPages ?? Math.ceil((meta.total ?? 0) / (meta.limit ?? rowsPerPage))) :
        1;
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 4 }, (_, i) => currentYear - i);
    const months = [
        'janvier',
        'février',
        'mars',
        'avril',
        'mai',
        'juin',
        'juillet',
        'août',
        'septembre',
        'octobre',
        'novembre',
        'décembre',
    ];
    const handleChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };
    const handleResetFilters = () => {
        setFilters({});
        setCurrentPage(1);
    };
    const handleExport = () => {
        if (loading || reports.length === 0) {
            return;
        }
        if (meta?.totalPages && meta.totalPages > 1) {
            setShowExportModal(true);
            return;
        }
        exportChargeReportToExcel(reports);
    };
    const handleExportAll = async () => {
        try {
            const data = await fetchChargeReports(undefined, filters, true);
            exportChargeReportToExcel(data.charges || []);
        }
        catch (e) {
            alert(e.message);
        }
        finally {
            setShowExportModal(false);
        }
    };
    const handleExportCurrent = () => {
        exportChargeReportToExcel(reports);
        setShowExportModal(false);
    };
    const isExportDisabled = loading || reports.length === 0;
    return (_jsxs("div", { className: "p-4 bg-gray-50 min-h-screen", children: [_jsx("div", { className: "flex flex-col gap-4 mb-4", children: _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2", children: [_jsx("input", { type: "date", value: filters.startDate ?? '', onChange: e => handleChange('startDate', e.target.value), className: "border p-2 rounded w-full sm:w-auto" }), _jsx("input", { type: "date", value: filters.endDate ?? '', onChange: e => handleChange('endDate', e.target.value), className: "border p-2 rounded w-full sm:w-auto" }), _jsxs("select", { value: filters.userId ?? '', onChange: e => handleChange('userId', e.target.value), className: "border p-2 rounded w-full sm:w-auto", children: [_jsx("option", { value: "", children: "Tous les utilisateurs" }), users.map(u => (_jsxs("option", { value: u.id, children: [u.first_name, " ", u.last_name] }, u.id)))] }), _jsxs("select", { value: filters.chargeType ?? '', onChange: e => handleChange('chargeType', e.target.value), className: "border p-2 rounded w-full sm:w-auto", children: [_jsx("option", { value: "", children: "Tous les types" }), chargeTypes.map(c => (_jsx("option", { value: c.id, children: c.label }, c.id)))] }), _jsxs("select", { value: filters.year ?? '', onChange: e => handleChange('year', e.target.value), className: "border p-2 rounded w-full sm:w-auto", children: [_jsx("option", { value: "", children: "Ann\u00E9e" }), years.map(year => (_jsx("option", { value: year, children: year }, year)))] }), _jsxs("select", { value: filters.month ?? '', onChange: e => handleChange('month', e.target.value), className: "border p-2 rounded w-full sm:w-auto", children: [_jsx("option", { value: "", children: "Mois" }), months.map((month, index) => (_jsx("option", { value: index + 1, children: month }, index + 1)))] }), _jsxs("div", { className: "flex flex-wrap gap-2 justify-end", children: [_jsxs("button", { onClick: handleResetFilters, title: "R\u00E9initialiser les filtres", "aria-label": "R\u00E9initialiser les filtres", className: "bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 w-full sm:w-auto", children: [_jsx(FaFilterCircleXmark, { "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: "R\u00E9initialiser les filtres" })] }), _jsxs("button", { onClick: handleExport, title: "Exporter en Excel", disabled: isExportDisabled, "aria-label": "Exporter en Excel", className: `bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full sm:w-auto ${isExportDisabled ? 'opacity-50 cursor-not-allowed' : ''}`, children: [_jsx(FaFileExcel, { "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: "Exporter en Excel" })] })] })] }) }), loading && _jsx("div", { children: "Chargement..." }), error && _jsxs("div", { className: "text-red-500", children: ["Erreur : ", error] }), !loading && !error && (_jsxs(_Fragment, { children: [_jsx("div", { className: "overflow-x-auto", children: _jsx(ChargeReportTable, { reports: reports, totalAmount: meta?.totalAmount ?? 0 }) }), meta && (_jsx(ChargeReportPagination, { currentPage: meta.page ?? 1, totalPages: totalPages, limit: rowsPerPage, onPageChange: (page) => setCurrentPage(page), onLimitChange: (value) => {
                            setRowsPerPage(value);
                            setCurrentPage(1);
                        } }))] })), showExportModal && (_jsx(ConfirmExportModal, { onExportAll: handleExportAll, onExportCurrent: handleExportCurrent, onCancel: () => setShowExportModal(false) }))] }));
};
export default ChargeReportPage;
